import { Component, Input, OnInit } from '@angular/core'
import { Organisation } from '@app/domain/Organisation'
import { Business } from '@app/domain/Business'
import { Venue } from '@app/domain/Venue'
import { Booking } from '@app/domain/Booking'
import { ProductLibrary } from '@app/domain/ProductLibrary'
import { ProductLibraryService } from '@app/features/management/services/product-library.service'
import { NgbActiveOffcanvas } from '@ng-bootstrap/ng-bootstrap'
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { Consumable, Menu, MenuItem } from '@app/domain/Consumable'
import { BookingDocket, CoverMenuChoice, CoverProductSelection } from '@app/domain/BookingDocket'
import { Modifier, ModifierGroup } from '@app/domain/Modifier'

interface EditableMenuItem {
  courseId: string
  courseName: string
  menuItem: MenuItem
  consumable: Consumable
  descriptionFull: string | null
  descriptionShort: string | null
  canTruncate: boolean
  expandedControlName: string
  modifierGroups: Array<{
    id: string
    name: string
    minimumModifiersRequired: number | null
    maximumModifiersRequired: number | null
    modifiers: Array<{
      modifier: Modifier
      primarySkuId: string | null
    }>
  }>
}

@Component({
  selector: 'app-booking-order-docket-edit',
  templateUrl: './booking-order-docket-edit.component.html',
  styles: [`
    :host
        display: flex
        flex-direction: column
        flex-grow: 1
        height: 100%
  `],
})
export class BookingOrderDocketEditComponent implements OnInit {
  @Input() organisation!: Organisation
  @Input() business!: Business
  @Input() venue!: Venue
  @Input() booking!: Booking
  @Input() editMode: 'choices' | 'limits' = 'choices'

  productLibrary: ProductLibrary | null = null
  menu: Menu | null = null
  form!: FormGroup
  coverForm: FormGroup | null = null
  menus: Menu[] = []
  items: EditableMenuItem[] = []
  draftCoverChoices: CoverMenuChoice[] = []
  editingCoverIndex: number | null = null
  validationErrors: string[] = []
  loading = true
  loadError = false
  characterLimit = 140

  constructor(
    private productLibraryService: ProductLibraryService,
    private fb: FormBuilder,
    public activeOffcanvas: NgbActiveOffcanvas
  ) {}

  ngOnInit() {
    this.form = this.fb.group({
      menuId: [this.booking.docket?.menuId ?? '', Validators.required],
      minimumCoursesRequired: [this.booking.docket?.minimumCoursesRequired ?? null, [Validators.min(0), this.wholeNumberValidator]],
      maximumCoursesRequired: [this.booking.docket?.maximumCoursesRequired ?? null, [Validators.min(0), this.wholeNumberValidator]]
    }, { validators: group => this.courseRangeValidator(group) })
    this.loadProductLibrary()
  }

  private loadProductLibrary() {
    this.loading = true
    this.loadError = false
    this.productLibraryService.getProductLibrary(this.organisation, this.business, this.venue)
      .subscribe({
        next: library => {
          this.productLibrary = library
          this.menus = library?.menus.filter(menu => menu.active) ?? []
          this.prepareExistingDocketEditor()
          this.form.updateValueAndValidity()
          this.loading = false
        },
        error: () => {
          this.loading = false
          this.loadError = true
        }
      })
  }

  submit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched()
      return
    }
    if (this.booking.docket) {
      if (!this.menu || !this.booking.docket) {
        return
      }
      if (this.editingCoverIndex !== null && !this.applyCoverEdit()) {
        return
      }
      this.activeOffcanvas.close(new BookingDocket(
        this.booking.docket.id,
        this.booking.docket.menuId,
        this.cloneCoverChoices(this.draftCoverChoices),
        this.booking.docket.dateCreated,
        new Date(),
        this.form.value.minimumCoursesRequired,
        this.form.value.maximumCoursesRequired
      ))
      return
    }
    this.activeOffcanvas.close(this.form.value)
  }

  cancel() {
    this.activeOffcanvas.dismiss('cancel')
  }

  private prepareExistingDocketEditor() {
    if (!this.booking.docket || !this.productLibrary) {
      this.menu = null
      this.items = []
      this.draftCoverChoices = []
      return
    }
    this.menu = this.productLibrary.getMenu(this.booking.docket.menuId)
    this.draftCoverChoices = this.cloneCoverChoices(this.booking.docket.coverChoices || [])
    this.buildEditableMenuItems()
  }

  private buildEditableMenuItems() {
    if (!this.menu || !this.productLibrary) {
      this.items = []
      return
    }
    this.items = [...this.menu.menuCourses]
      .sort((a, b) => a.orderIndex - b.orderIndex)
      .flatMap(course => [...course.menuItems]
        .sort((a, b) => a.orderIndex - b.orderIndex)
        .flatMap(menuItem => {
          const consumable = this.productLibrary!.getConsumable(menuItem.consumableId)
          if (!consumable) {
            return []
          }
          const description = consumable.description
          return [{
            courseId: course.id,
            courseName: course.displayName,
            menuItem,
            consumable,
            descriptionFull: description,
            descriptionShort: description ? description.slice(0, this.characterLimit) : null,
            canTruncate: !!description && description.length > this.characterLimit,
            expandedControlName: menuItem.id,
            modifierGroups: consumable.modifierGroupIds
              .map(id => this.productLibrary!.modifierGroups.find(group => group.id === id) || null)
              .filter((group): group is ModifierGroup => !!group)
              .map(modifierGroup => ({
                id: modifierGroup.id,
                name: modifierGroup.name,
                minimumModifiersRequired: modifierGroup.minimumModifiersRequired,
                maximumModifiersRequired: modifierGroup.maximumModifiersRequired,
                modifiers: modifierGroup.modifiers.map(modifier => ({
                  modifier,
                  primarySkuId: modifier.skus[0]?.id ?? null,
                }))
              }))
          }]
        }))
  }

  editCover(index: number) {
    this.editingCoverIndex = index
    this.validationErrors = []
    this.buildCoverForm(index)
  }

  cancelCoverEdit() {
    this.editingCoverIndex = null
    this.coverForm = null
    this.validationErrors = []
  }

  applyCoverEdit(): boolean {
    if (this.editingCoverIndex === null || !this.coverForm) {
      return true
    }
    const selections = this.buildSelections()
    const errors = this.validateSelections(selections)
    if (errors.length > 0) {
      this.validationErrors = errors
      this.coverForm.markAllAsTouched()
      return false
    }
    this.draftCoverChoices[this.editingCoverIndex] = new CoverMenuChoice(
      this.coverForm.value.name?.trim() || null,
      this.coverForm.value.notes?.trim() || null,
      selections
    )
    this.cancelCoverEdit()
    return true
  }

  addEmptyCoverEntry() {
    if (!this.canAddMissingCovers) {
      return
    }
    this.draftCoverChoices.push(new CoverMenuChoice(null, null, []))
  }

  addMissingCoverEntries() {
    while (this.canAddMissingCovers) {
      this.addEmptyCoverEntry()
    }
  }

  deleteCover(index: number) {
    this.draftCoverChoices.splice(index, 1)
    if (this.editingCoverIndex === index) {
      this.cancelCoverEdit()
      return
    }
    if (this.editingCoverIndex !== null && this.editingCoverIndex > index) {
      this.editingCoverIndex -= 1
    }
  }

  toggleDescription(menuItemId: string) {
    const control = this.coverForm?.get(['ui', 'expandedDescriptions', menuItemId].join('.')) as FormControl<boolean> | null
    if (!control) {
      return
    }
    control.setValue(!control.value)
    control.markAsDirty()
    control.markAsTouched()
  }

  get canAddMissingCovers(): boolean {
    return this.draftCoverChoices.length < Math.max(this.booking.size || 0, 0)
  }

  get missingCoverCount(): number {
    return Math.max((this.booking.size || 0) - this.draftCoverChoices.length, 0)
  }

  get completedCoverCount(): number {
    return this.draftCoverChoices.filter(choice => choice.selections.length > 0).length
  }

  get productSelectionGroup(): FormGroup | null {
    return this.coverForm?.get('productSelection') as FormGroup | null
  }

  get modifierSelectionGroup(): FormGroup | null {
    return this.coverForm?.get('modifierSelection') as FormGroup | null
  }

  get itemsByCourse(): Array<{ courseId: string, courseName: string, items: EditableMenuItem[] }> {
    const courses = new Map<string, { courseName: string, items: EditableMenuItem[] }>()
    for (const item of this.items) {
      if (!courses.has(item.courseId)) {
        courses.set(item.courseId, { courseName: item.courseName, items: [] })
      }
      courses.get(item.courseId)!.items.push(item)
    }
    return Array.from(courses.entries()).map(([courseId, course]) => ({
      courseId,
      courseName: course.courseName,
      items: course.items,
    }))
  }

  get selectedCount(): number {
    return this.buildSelections().length
  }

  isProductSelected(menuItemId: string): boolean {
    const control = this.productSelectionGroup?.get(menuItemId) as FormControl<boolean> | null
    return control?.value === true
  }

  getChoiceTitle(choice: CoverMenuChoice, index: number): string {
    const name = choice.name?.trim()
    return name && name.length > 0 ? name : `Person ${index + 1}`
  }

  getChoiceSelections(choice: CoverMenuChoice): string[] {
    if (!this.productLibrary || !this.menu) {
      return []
    }
    return (choice.selections || []).map(selection => {
      const productName = this.productLibrary!.resolveProductName(selection.productSkuId, this.menu) ?? 'Item'
      const modifierNames = (selection.modifierSkuIds || [])
        .map(id => this.productLibrary!.resolveModifierName(id))
        .filter((name): name is string => !!name)
      return modifierNames.length > 0
        ? `${productName} (${modifierNames.join(', ')})`
        : productName
    })
  }

  private wholeNumberValidator(control: AbstractControl) {
    return control.value === null || Number.isInteger(control.value) ? null : { wholeNumber: true }
  }

  private courseRangeValidator(group: AbstractControl) {
    const menu = this.productLibrary?.getMenu(group.get('menuId')?.value)
    const min = group.get('minimumCoursesRequired')?.value ?? menu?.minimumCoursesRequired ?? null
    const max = group.get('maximumCoursesRequired')?.value ?? menu?.maximumCoursesRequired ?? null
    return min !== null && max !== null && min > max ? { minGreaterThanMax: true } : null
  }

  get selectedMenu(): Menu | null {
    return this.productLibrary?.getMenu(this.form?.value.menuId) ?? null
  }

  get courseCountOptions(): number[] {
    const menu = this.selectedMenu
    const courseCount = menu?.menuCourses.filter(course => !course.excludesFromCourseCount).length ?? 0
    const values = [
      ...Array.from({ length: courseCount + 1 }, (_, count) => count),
      menu?.minimumCoursesRequired, menu?.maximumCoursesRequired,
      this.form?.value.minimumCoursesRequired, this.form?.value.maximumCoursesRequired
    ]
    // Keep saved limits selectable even if courses have since been removed.
    return [...new Set(values.filter((value): value is number => Number.isInteger(value) && value >= 0))].sort((a, b) => a - b)
  }

  get effectiveMinimumCoursesRequired(): number | null {
    return this.form?.value.minimumCoursesRequired ?? this.selectedMenu?.minimumCoursesRequired ?? null
  }

  get effectiveMaximumCoursesRequired(): number | null {
    return this.form?.value.maximumCoursesRequired ?? this.selectedMenu?.maximumCoursesRequired ?? null
  }

  get hasCourseLimits(): boolean {
    return this.effectiveMinimumCoursesRequired !== null || this.effectiveMaximumCoursesRequired !== null
  }

  get courseLimitsDisplay(): string | null {
    if (!this.selectedMenu || !this.hasCourseLimits) {
      return null
    }
    const min = this.effectiveMinimumCoursesRequired
    const max = this.effectiveMaximumCoursesRequired
    if (min !== null && max !== null) {
      return min === max ? `Choose exactly ${min} courses per guest` : `Choose ${min}-${max} courses per guest`
    }
    return min !== null ? `Choose at least ${min} courses per guest` : `Choose up to ${max} courses per guest`
  }

  isCourseExcludedFromCount(courseId: string): boolean {
    return this.menu?.menuCourses.some(course => course.id === courseId && course.excludesFromCourseCount) ?? false
  }

  getCourseLimitsDisplay(courseId: string): string | null {
    const course = this.menu?.menuCourses.find(c => c.id === courseId)
    if (!course) {
      return null
    }
    const min = course.minimumItemsRequired
    const max = course.maximumItemsRequired
    if (min !== null && max !== null) {
      return min === max ? `Select exactly ${min}` : `Select ${min}-${max}`
    }
    if (min !== null) {
      return `Select at least ${min}`
    }
    if (max !== null) {
      return `Select up to ${max}`
    }
    return null
  }

  getModifierGroupLimitsDisplay(modifierGroup: { minimumModifiersRequired: number | null, maximumModifiersRequired: number | null }): string | null {
    const min = modifierGroup.minimumModifiersRequired
    const max = modifierGroup.maximumModifiersRequired
    if (min !== null && max !== null) {
      return min === max ? `Select exactly ${min}` : `Select ${min}-${max}`
    }
    if (min !== null) {
      return `Select at least ${min}`
    }
    if (max !== null) {
      return `Select up to ${max}`
    }
    return null
  }

  getSelectedCountForCourse(courseId: string): number {
    const selections = this.buildSelections()
    const course = this.menu?.menuCourses.find(c => c.id === courseId)
    if (!course) {
      return 0
    }
    const courseItemIds = new Set(course.menuItems.map(item => item.id))
    return selections.filter(selection => courseItemIds.has(selection.menuItemId!)).length
  }

  getCourseValidationError(courseId: string): string | null {
    if (this.selectedCount === 0) {
      return null
    }
    const course = this.menu?.menuCourses.find(c => c.id === courseId)
    if (!course) {
      return null
    }
    const selectedCount = this.getSelectedCountForCourse(courseId)
    if (this.hasCourseLimits && selectedCount === 0) {
      return null
    }
    if (course.minimumItemsRequired !== null && selectedCount < course.minimumItemsRequired) {
      return `Please select at least ${course.minimumItemsRequired}`
    }
    if (course.maximumItemsRequired !== null && selectedCount > course.maximumItemsRequired) {
      return `Please select no more than ${course.maximumItemsRequired}`
    }
    return null
  }

  getModifierGroupValidationError(menuItemId: string, modifierGroup: EditableMenuItem['modifierGroups'][number]): string | null {
    if (!this.isProductSelected(menuItemId)) {
      return null
    }
    const min = modifierGroup.minimumModifiersRequired
    const max = modifierGroup.maximumModifiersRequired
    if (min === null && max === null) {
      return null
    }
    const productModifierGroup = this.modifierSelectionGroup?.get(menuItemId) as FormGroup | null
    if (!productModifierGroup) {
      return null
    }
    const modifierSkuIds = modifierGroup.modifiers
      .map(modifier => modifier.primarySkuId)
      .filter((id): id is string => id !== null)
    const selectedCount = modifierSkuIds.filter(skuId => productModifierGroup.get(skuId)?.value === true).length
    if (min !== null && selectedCount < min) {
      return `Please select at least ${min}`
    }
    if (max !== null && selectedCount > max) {
      return `Please select no more than ${max}`
    }
    return null
  }

  private buildCoverForm(index: number) {
    const choice = this.draftCoverChoices[index] || new CoverMenuChoice(null, null, [])
    const expandedDescriptions = this.items.reduce<Record<string, FormControl<boolean>>>((acc, item) => {
      acc[item.expandedControlName] = new FormControl(false, { nonNullable: true })
      return acc
    }, {})
    this.coverForm = this.fb.group({
      name: new FormControl<string | null>(choice.name),
      notes: new FormControl<string | null>(choice.notes),
      productSelection: this.fb.group({}),
      modifierSelection: this.fb.group({}),
      ui: this.fb.group({
        expandedDescriptions: this.fb.group(expandedDescriptions),
      })
    })
    const productSelectionGroup = this.productSelectionGroup!
    const modifierSelectionGroup = this.modifierSelectionGroup!
    const chosenByItem = new Map(choice.selections.flatMap(selection => {
      const item = this.menu!.findItemForSelection(selection)
      return item ? [[item.id, selection] as const] : []
    }))
    this.items.forEach(item => {
      productSelectionGroup.addControl(
        item.menuItem.id,
        new FormControl<boolean>(chosenByItem.has(item.menuItem.id), { nonNullable: true })
      )
      const modifierSkuControls = new FormGroup({})
      const selectedModifierSkuIds = new Set(chosenByItem.get(item.menuItem.id)?.modifierSkuIds ?? [])
      item.modifierGroups.forEach(group => {
        group.modifiers.forEach(({ primarySkuId }) => {
          if (!primarySkuId) {
            return
          }
          modifierSkuControls.addControl(
            primarySkuId,
            new FormControl<boolean>(selectedModifierSkuIds.has(primarySkuId), { nonNullable: true })
          )
        })
      })
      modifierSelectionGroup.addControl(item.menuItem.id, modifierSkuControls)
    })
  }

  private buildSelections(): CoverProductSelection[] {
    const productSelectionGroup = this.productSelectionGroup
    const modifierSelectionGroup = this.modifierSelectionGroup
    if (!productSelectionGroup || !modifierSelectionGroup) {
      return []
    }
    const selectedProducts = Object.entries(productSelectionGroup.value)
      .filter(([, selected]) => !!selected)
      .map(([menuItemId]) => menuItemId)
    return selectedProducts.map(menuItemId => {
      const modifierGroup = modifierSelectionGroup.get(menuItemId) as FormGroup | null
      const modifierSkuIds = modifierGroup
        ? Object.entries(modifierGroup.value)
          .filter(([, selected]) => !!selected)
          .map(([modifierSkuId]) => modifierSkuId)
        : []
      const item = this.items.find(item => item.menuItem.id === menuItemId)!
      return new CoverProductSelection(item.menuItem.skuId, modifierSkuIds, menuItemId)
    })
  }

  private validateSelections(selections: CoverProductSelection[]): string[] {
    if (selections.length === 0 || !this.menu) {
      return []
    }
    const errors: string[] = []
    const selectedItemIds = new Set(selections.map(selection => selection.menuItemId))
    const selectedCourses = this.menu.menuCourses.filter(course => !course.excludesFromCourseCount && course.menuItems.some(item => selectedItemIds.has(item.id))).length
    if (this.effectiveMinimumCoursesRequired !== null && selectedCourses < this.effectiveMinimumCoursesRequired) {
      errors.push(`Select at least ${this.effectiveMinimumCoursesRequired} courses`)
    }
    if (this.effectiveMaximumCoursesRequired !== null && selectedCourses > this.effectiveMaximumCoursesRequired) {
      errors.push(`Select no more than ${this.effectiveMaximumCoursesRequired} courses`)
    }
    this.menu.menuCourses.forEach(course => {
      const courseItemIds = new Set(course.menuItems.map(item => item.id))
      const selectedCount = selections.filter(selection => courseItemIds.has(selection.menuItemId!)).length
      if (this.hasCourseLimits && selectedCount === 0) {
        return
      }
      if (course.minimumItemsRequired !== null && selectedCount < course.minimumItemsRequired) {
        errors.push(`${course.displayName}: select at least ${course.minimumItemsRequired}`)
      }
      if (course.maximumItemsRequired !== null && selectedCount > course.maximumItemsRequired) {
        errors.push(`${course.displayName}: select no more than ${course.maximumItemsRequired}`)
      }
    })
    selections.forEach(selection => {
      const item = this.items.find(menuItem => menuItem.menuItem.id === selection.menuItemId)
      if (!item) {
        return
      }
      item.modifierGroups.forEach(group => {
        const groupModifierSkuIds = group.modifiers
          .map(modifier => modifier.primarySkuId)
          .filter((id): id is string => id !== null)
        const selectedCount = groupModifierSkuIds.filter(id => selection.modifierSkuIds.includes(id)).length
        if (group.minimumModifiersRequired !== null && selectedCount < group.minimumModifiersRequired) {
          errors.push(`${item.consumable.displayName} - ${group.name}: select at least ${group.minimumModifiersRequired}`)
        }
        if (group.maximumModifiersRequired !== null && selectedCount > group.maximumModifiersRequired) {
          errors.push(`${item.consumable.displayName} - ${group.name}: select no more than ${group.maximumModifiersRequired}`)
        }
      })
    })
    return errors
  }

  private cloneCoverChoices(choices: CoverMenuChoice[]): CoverMenuChoice[] {
    return choices.map(choice => new CoverMenuChoice(
      choice.name ?? null,
      choice.notes ?? null,
      (choice.selections || []).map(selection => new CoverProductSelection(
        selection.productSkuId,
        [...(selection.modifierSkuIds || [])],
        selection.menuItemId ?? null
      ))
    ))
  }
}
